import { UnitDisplayFormat } from '@/ApiClient';

/**
 * Converts a decimal number to a dyadic fraction string.
 * Dyadic fractions have denominators that are powers of 2 (2, 4, 8, 16, 32, 64).
 *
 * Examples: 3.25 → "3 1/4", 0.5 → "1/2", 3.0 → "3", -2.75 → "-2 3/4"
 */
export function decimalToDyadicFraction(value: number, maxDenominator = 64): string {
  if (value === 0) return '0';

  const negative = value < 0;
  const absValue = Math.abs(value);
  const wholePart = Math.floor(absValue);
  const fractionalPart = absValue - wholePart;

  if (fractionalPart < 1e-9) {
    return negative ? `-${wholePart}` : `${wholePart}`;
  }

  // Find the best dyadic fraction approximation
  let bestNumerator = 0;
  let bestDenominator = 1;
  let bestError = fractionalPart;

  for (let denom = 2; denom <= maxDenominator; denom *= 2) {
    const numer = Math.round(fractionalPart * denom);
    if (numer > 0 && numer < denom) {
      const error = Math.abs(fractionalPart - numer / denom);
      if (error < bestError) {
        bestNumerator = numer;
        bestDenominator = denom;
        bestError = error;
      }
    }
  }

  // If no good fraction found (value doesn't map cleanly), fall back to rounded decimal
  if (bestNumerator === 0 || bestError > 1e-9) {
    const rounded = parseFloat(absValue.toFixed(4));
    return negative ? `-${rounded}` : `${rounded}`;
  }

  // Simplify the fraction
  const gcd = greatestCommonDivisor(bestNumerator, bestDenominator);
  const simplifiedNumerator = bestNumerator / gcd;
  const simplifiedDenominator = bestDenominator / gcd;

  const fractionStr = `${simplifiedNumerator}/${simplifiedDenominator}`;
  const prefix = negative ? '-' : '';

  if (wholePart === 0) {
    return `${prefix}${fractionStr}`;
  }
  return `${prefix}${wholePart} ${fractionStr}`;
}

/**
 * Parses a dyadic fraction string (or decimal string) to a number.
 * Accepts: "3 1/4", "1/2", "3", "3.25", "3,25", "-2 3/4"
 * Returns null for invalid input.
 */
export function dyadicFractionToDecimal(input: string): number | null {
  const trimmed = input.trim();
  if (trimmed === '') return null;

  // A lone comma is a decimal separator on most of the keyboards this is typed on. Only when there
  // is no dot as well — "1,234.5" and "1.234,5" are ambiguous enough to be worth rejecting rather
  // than guessing at.
  const normalised = trimmed.includes('.') ? trimmed : trimmed.replace(',', '.');

  // Try plain number first
  const plainNumber = Number(normalised);
  if (!isNaN(plainNumber) && isFinite(plainNumber)) {
    return plainNumber;
  }

  // Parse fraction formats: "3 1/4", "1/4", "-3 1/4", "-1/4"
  const match = trimmed.match(/^(-?)(\d+)?\s*(\d+)\/(\d+)$/);
  if (!match) return null;

  const sign = match[1] === '-' ? -1 : 1;
  const wholePart = match[2] ? parseInt(match[2], 10) : 0;
  const numerator = parseInt(match[3], 10);
  const denominator = parseInt(match[4], 10);

  if (denominator === 0) return null;

  return sign * (wholePart + numerator / denominator);
}

/**
 * Formats a numeric value according to the unit's display format.
 */
export function formatValue(value: number | null | undefined, displayFormat: UnitDisplayFormat): string | null {
  if (value === null || value === undefined) return null;
  if (displayFormat === UnitDisplayFormat.DyadicFraction) {
    return decimalToDyadicFraction(value);
  }
  return parseFloat(value.toFixed(4)).toString();
}

/**
 * Formats a diff value with sign prefix, using the appropriate display format.
 */
export function formatDiff(diff: number, displayFormat: UnitDisplayFormat): string {
  if (diff === 0) return '0';
  if (displayFormat === UnitDisplayFormat.DyadicFraction) {
    const formatted = decimalToDyadicFraction(Math.abs(diff));
    return diff > 0 ? `+${formatted}` : `-${formatted}`;
  }
  const rounded = parseFloat(diff.toFixed(4));
  return rounded > 0 ? `+${rounded}` : `${rounded}`;
}

export function formatTolerance(
  upperValue: number,
  lowerValue: number,
  displayFormat: UnitDisplayFormat,
  unitSymbol: string
): string {
  if (upperValue === 0 && lowerValue === 0) return '';
  const formatVal = (v: number) =>
    displayFormat === UnitDisplayFormat.DyadicFraction ? decimalToDyadicFraction(v) : v.toFixed(1).replace(/\.0$/, '');
  if (upperValue === lowerValue) return `± ${formatVal(upperValue)} ${unitSymbol}`;
  return `+${formatVal(upperValue)}/-${formatVal(lowerValue)} ${unitSymbol}`;
}

export type ToleranceStatus = 'ok' | 'edge' | 'out';

/**
 * Checks whether a diff value is within tolerance, on the edge, or out of bounds.
 * Positive diffs are checked against upperValue, negative diffs against lowerValue.
 */
export function getToleranceStatus(diff: number, upperValue: number, lowerValue: number): ToleranceStatus {
  if (diff === 0) return 'ok';
  if (upperValue <= 0 && lowerValue <= 0) return 'ok';
  const limit = diff > 0 ? upperValue : lowerValue;
  if (limit <= 0) return 'ok';
  const absDiff = Math.abs(diff);
  if (absDiff > limit + 1e-9) return 'out';
  if (absDiff >= limit - 1e-9) return 'edge';
  return 'ok';
}

function greatestCommonDivisor(a: number, b: number): number {
  while (b !== 0) {
    [a, b] = [b, a % b];
  }
  return a;
}
